import { GRADE_ORDER, type Grade } from "../types/standards";
import type { SummaryTarget } from "./types";

const SCOPE_KEYS = [
  "summaryLevel",
  "summaryTeacher",
  "summaryGrade",
  "summaryGroup",
  "summaryStudent",
] as const;

function positiveId(value: string | null): number | null {
  if (value == null || !/^[1-9]\d*$/.test(value)) return null;
  const number = Number(value);
  return Number.isSafeInteger(number) ? number : null;
}

export function readSummaryTarget(
  params: URLSearchParams,
  defaultTarget: SummaryTarget,
): SummaryTarget | null {
  if (SCOPE_KEYS.some((key) => params.getAll(key).length > 1)) return null;
  const level = params.get("summaryLevel");
  if (!level)
    return SCOPE_KEYS.some((key) => params.has(key)) ? null : defaultTarget;
  const teacherUserId = positiveId(params.get("summaryTeacher"));
  const studentUserId = positiveId(params.get("summaryStudent"));
  const groupId = positiveId(params.get("summaryGroup"));
  const rawGrade = params.get("summaryGrade");
  const grade = GRADE_ORDER.includes(rawGrade as Grade)
    ? (rawGrade as Grade)
    : null;
  const noStudent = !params.has("summaryStudent");
  const noGroup = !params.has("summaryGroup");
  const noGrade = !params.has("summaryGrade");
  if (level === "school") {
    return !params.has("summaryTeacher") && noStudent && noGroup && noGrade
      ? { level }
      : null;
  }
  if (!teacherUserId) return null;
  if (level === "teacher" && noStudent && noGroup && noGrade)
    return { level, teacherUserId };
  if (level === "grade" && grade && noStudent && noGroup)
    return { level, teacherUserId, grade };
  if (level === "group" && groupId && noStudent && noGrade)
    return { level, teacherUserId, groupId };
  if (
    level === "student" &&
    studentUserId &&
    (noGroup || groupId) &&
    (noGrade || grade)
  ) {
    return {
      level,
      teacherUserId,
      studentUserId,
      ...(groupId ? { groupId } : {}),
      ...(grade ? { grade } : {}),
    };
  }
  return null;
}

export function summarySearch(
  current: URLSearchParams,
  target: SummaryTarget,
  view: "summary" | "heatmap" = "summary",
): URLSearchParams {
  const next = new URLSearchParams(current);
  SCOPE_KEYS.forEach((key) => next.delete(key));
  next.set("tab", "standards");
  next.set("standardsView", view);
  next.set("summaryLevel", target.level);
  if (target.level !== "school")
    next.set("summaryTeacher", String(target.teacherUserId));
  if (
    target.level === "grade" ||
    (target.level === "student" && target.grade)
  ) {
    next.set("summaryGrade", target.grade!);
  }
  if (
    target.level === "group" ||
    (target.level === "student" && target.groupId)
  ) {
    next.set("summaryGroup", String(target.groupId));
  }
  if (target.level === "student")
    next.set("summaryStudent", String(target.studentUserId));
  return next;
}

export function summaryTargetKey(target: SummaryTarget): string {
  return summarySearch(new URLSearchParams(), target).toString();
}
